import { useState } from 'react'

import map from 'lodash/map'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString } from 'modules/core/lib/dates'

import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import CommonSelectorsForm, {
  TIME_PERIODS,
} from 'modules/shift_management/lib/forms/CommonSelectorsForm'
import { Shift } from 'modules/shift_management/lib/types'

import { getWeekDay } from './useDateRangeSelection'
import useDefaultAttributesFromRows from './useDefaultAttributesFromRows'
import useDefaultValues from './useDefaultValues'
import useRowAndEmployeeSelection from './useRowAndEmployeeSelection'
import { useSelectionState } from './useSelection'

const useEditEmployees = (shifts: Shift[], mixedValues: boolean) => {
  const [{ view }] = useSchedule()
  const { daysByRow } = useSelectionState()

  // We show the employee selector only if editing shifts from the same employee
  // and we need to include the original employee in case the user wants to
  // edit original and create new ones for other employees at the same time
  const shiftsEmployeesIds = uniq(map(shifts, (shift: Shift) => shift.employeeId))
  const originalId = shiftsEmployeesIds.length === 1 ? shiftsEmployeesIds[0] : undefined
  const originalEmployeeId = originalId ? [originalId] : []
  const [selectedEmployeeIds, setSelectedEmployees] = useState<number[] | []>(originalEmployeeId)
  const commonSelectorsForm = useLazyRef<CommonSelectorsForm>(
    () =>
      new CommonSelectorsForm({
        start_date: dateToString(view.firstDay, 'backend'),
        end_date: dateToString(view.lastDay, 'backend'),
        apply_on_days: view.isDaily ? getWeekDay(view.startAt) : getWeekDay(view.currentDateString),
        time_period: TIME_PERIODS.DOES_NOT_REPEAT,
        employee_id: shiftsEmployeesIds.length ? shiftsEmployeesIds[0] : undefined,
      })
  ).current

  const editingOneEmployee = shiftsEmployeesIds.length === 1
  const selectingOneEmployee = selectedEmployeeIds.length === 1
  const editingOriginal =
    editingOneEmployee && selectingOneEmployee && originalId === selectedEmployeeIds[0]

  // This is needed to separate the actions between edit + create OR delete + create
  const isOriginalIncluded = !!selectedEmployeeIds.filter((id) => id === originalId).length

  // As daysByRow depend on the selection reducer and only the original employee
  // is included, we need to use the method below with the new ids from the
  // employee selector input to create the days for those employees
  const reassignEmployeesIds = selectedEmployeeIds.filter((id) => id !== originalId)

  // For the custom shifts, we do not want to include the original employee
  // as we edit the original shift in a separate action from creating new shifts
  const daysByEmployeeCustom = useRowAndEmployeeSelection(daysByRow, reassignEmployeesIds)

  // For the saved shifts, we delete all original shifts and re-create them
  // from scratch, so the final id list will depend on the user selection
  // if we are editing shifts from only 1 employee
  const finalEmployeesIds = isOriginalIncluded
    ? shiftsEmployeesIds.concat(selectedEmployeeIds)
    : reassignEmployeesIds

  // To know if we are editing multiple employees, we check if
  // originalId exists. If it does, we need the finalEmployees logic above.
  // Otherwise, all we need is the daysByRow coming from the selection reducer
  const withOriginalEmployee = useRowAndEmployeeSelection(daysByRow, finalEmployeesIds)
  const daysByEmployeeSaved = originalId ? withOriginalEmployee : daysByRow

  const preselectedSavedShifts = map(uniqBy(shifts, 'defaultShiftId'), 'defaultShiftId')
  const mixedDefaultShifts = preselectedSavedShifts.length > 1
  const mixedTemplateShifts = map(uniqBy(shifts, 'templateWeekId'), 'templateWeekId').length > 1

  const canReassignShifts =
    editingOneEmployee && !mixedValues && !mixedDefaultShifts && !mixedTemplateShifts

  const showReassignMessage = editingOneEmployee && !editingOriginal

  const defaultAttributes = useDefaultAttributesFromRows()

  const { weekDays } = useDefaultValues(shiftsEmployeesIds, commonSelectorsForm)

  const hasMultipleEmployees = shiftsEmployeesIds.length > 1
  const selectedAreas = Object.values(defaultAttributes).flat()
  const hasMultipleAreas =
    uniqBy(selectedAreas, (x) => [x.location_id, x.work_area_id].join('-')).length > 1

  const canReplaceShifts = view.isByEmployees || (!hasMultipleEmployees && !hasMultipleAreas)

  return {
    selectedEmployeeIds,
    setSelectedEmployees,
    editingOneEmployee,
    editingOriginal,
    isOriginalIncluded,
    daysByEmployeeCustom,
    daysByEmployeeSaved,
    canReassignShifts,
    canReplaceShifts,
    showReassignMessage,
    preselectedSavedShifts,
    commonSelectorsForm,
    weekDays,
    defaultAttributes,
  }
}

export default useEditEmployees
